import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { EventTypes } from "@max/common/event";
import { useEventConfig } from "../EventConfig";
import { useLightShow } from "./LightShow";
import phoneGlowCardAnimated from "./PhoneGlowCardAnimated.svg";
import grungeButton from "./GrungeButton.svg";
import grungeEdgeH from "./GrungeEdgeH.svg";
import grungeEdgeV from "./GrungeEdgeV.svg";

/**
 * "Light up the crowd" rejoin bar, shown under the template header after a fan
 * declines or exits a live light show. Each template places it in its layout;
 * the styling follows the per-template comps (ENG-2291).
 */
export const LightShowBar = () => {
  const { t } = useTranslation();
  const { type } = useEventConfig();
  const { showRejoin, needsCheckIn, join } = useLightShow();

  if (!showRejoin) {
    return null;
  }

  return (
    <Slot $type={type}>
      <Bar $type={type}>
        {/* <img> so the SVG's own glow animation runs untouched. */}
        <Icon src={phoneGlowCardAnimated} width={24} height={43} alt="" />
        <Text>
          <Title>{t("Light up the crowd")}</Title>
          <Subtitle>{t("Your phone is part of the show")}</Subtitle>
        </Text>
        <JoinButton $type={type} onClick={join}>
          {needsCheckIn ? t("Check In to Join") : t("Join in")}
        </JoinButton>
      </Bar>
    </Slot>
  );
};

const slotStyles: Record<EventTypes, ReturnType<typeof css>> = {
  [EventTypes.Swiper]: css`
    margin: 0 20px 26px;
  `,
  // Round's Content has margin-top: -1px, which would cover the bar's bottom
  // border without the 1px bottom padding.
  [EventTypes.Round]: css`
    padding: 13px 12px 1px;
    background-color: #000;
  `,
  [EventTypes.Cards]: css`
    margin: 20px 20px 0;
  `,
  [EventTypes.Boxy]: css`
    margin: 5px 20px 12px;
  `,
  [EventTypes.Grunge]: css`
    margin: 4px 20px 0;
  `,
};

const barStyles: Record<EventTypes, ReturnType<typeof css>> = {
  [EventTypes.Swiper]: css`
    background: var(--template-primary);
    border-radius: 21px;
  `,
  [EventTypes.Round]: css`
    border: 1px solid var(--template-primary);
    border-radius: 15px;
  `,
  [EventTypes.Cards]: css`
    background: var(--template-primary);
    border-radius: 12px;
  `,
  [EventTypes.Boxy]: css`
    border: 2px solid #999999;
  `,
  // Rough painted frame: ragged strips tiled along each edge as a mask over the
  // theme color, the same mask technique the Grunge header uses for its torn edge.
  [EventTypes.Grunge]: css`
    /* Special Elite only ships a regular weight; 600 renders as faux bold. */
    font-weight: 400;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: var(--template-primary);
      mask-image:
        url(${grungeEdgeH}), url(${grungeEdgeH}), url(${grungeEdgeV}),
        url(${grungeEdgeV});
      mask-position:
        0 0,
        73px 100%,
        0 0,
        100% 91px;
      mask-size:
        auto 6px,
        auto 6px,
        6px auto,
        6px auto;
      mask-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
    }
  `,
};

const buttonStyles: Record<EventTypes, ReturnType<typeof css>> = {
  [EventTypes.Swiper]: css`
    background: var(--template-secondary);
    color: var(--template-primary);
    border-radius: 50px;
  `,
  [EventTypes.Round]: css`
    background: var(--template-primary);
    color: var(--template-secondary);
    border-radius: 50px;
  `,
  // Matches the Cards template's Check In button so it reads on any theme.
  [EventTypes.Cards]: css`
    background: var(--template-secondary);
    color: var(--template-primary);
    border-radius: 8px;
    font-size: 17px;
    font-weight: 800;
  `,
  [EventTypes.Boxy]: css`
    background: var(--template-primary);
    color: var(--template-text);
  `,
  // Brush-stroke shape from the comps, masked so it takes the theme color.
  [EventTypes.Grunge]: css`
    min-width: 101px;
    height: 41px;
    background: var(--template-primary);
    color: var(--template-secondary);
    font-weight: 400;
    mask-image: url(${grungeButton});
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
  `,
};

const Slot = styled.div<{ $type: EventTypes }>`
  position: relative;
  z-index: 1;
  ${({ $type }) => slotStyles[$type]};
`;

const Bar = styled.div<{ $type: EventTypes }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 76px;
  padding: 10px 20px 10px 22px;
  color: var(--template-text);
  font-family: var(--template-font-family);
  font-weight: 600;
  ${({ $type }) => barStyles[$type]};
`;

// The SVG reserves room for the rays above the phone, so centering the image
// leaves the phone body ~5.5px low (7.4 viewBox units × 0.75 scale). Shift it
// so the phone lines up with the text and the rays sit above.
const Icon = styled.img`
  display: block;
  flex-shrink: 0;
  transform: translateY(-5.5px);
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const Title = styled.span`
  font-size: 16px;
  line-height: 20px;
`;

const Subtitle = styled.span`
  font-size: 10px;
  line-height: 14px;
`;

const JoinButton = styled.button<{ $type: EventTypes }>`
  flex-shrink: 0;
  min-width: 97px;
  height: 36px;
  padding: 0 14px;
  border: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  ${({ $type }) => buttonStyles[$type]};
`;
